import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, X, FileText, Calendar } from 'lucide-react';
import SimpleEditor from '../../components/SimpleEditor';
import { useArticles } from '../../hooks/useArticles';
import AdminLayout from '../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: var(--text-dark);
  font-weight: 700;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-blue);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: #004d8f;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: var(--off-white);
  font-weight: 600;
  color: var(--text-dark);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--border-light);
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$category) {
      case 'Médiation': return '#FEE2E2';
      case 'Publication': return '#DBEAFE';
      case 'Coopération': return '#D1FAE5';
      case 'Formation': return '#FEF3C7';
      case 'Événement': return '#F3E8FF';
      case 'Visite': return '#FED7AA';
      default: return '#F3F4F6';
    }
  }};
  color: ${props => {
    switch (props.$category) {
      case 'Médiation': return '#DC2626';
      case 'Publication': return '#2563EB';
      case 'Coopération': return '#059669';
      case 'Formation': return '#D97706';
      case 'Événement': return '#9333EA';
      case 'Visite': return '#C2410C';
      default: return '#6B7280';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: var(--transition);
  margin-right: 0.5rem;

  &:hover {
    background: var(--background-light);
  }

  &.delete:hover {
    background: #FEE2E2;
    color: #DC2626;
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-dark);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--primary-blue);
  color: white;
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: #004d8f;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--background-light);
  color: var(--text-dark);
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: var(--border-light);
  }
`;

const CloseButton = styled.button`
  background: none;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const ErrorMessage = styled.div`
  background: #FEE2E2;
  color: #DC2626;
  padding: 0.75rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
`;

const ImageUploadBox = styled.div`
  border: 2px dashed var(--border-light);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: var(--transition);
  background: ${props => props.$hasImage ? 'transparent' : 'var(--background-light)'};
  
  &:hover {
    border-color: var(--primary-blue);
  }
  
  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: var(--radius-md);
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 0.8rem;
  color: var(--text-light);
  margin-top: 0.25rem;
`;

const categories = [
  'Médiation',
  'Publication', 
  'Coopération',
  'Formation',
  'Événement',
  'Visite'
];

const AdminArticles = () => {
  const { articles, loading, createArticle, editArticle, removeArticle } = useArticles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    resume: '',
    content: '',
    category: categories[0],
    readTime: '3 min',
    featuredImage: ''
  });
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingContentImage, setUploadingContentImage] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim() || !formData.resume.trim()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    let result;
    if (editingArticle) {
      result = await editArticle(editingArticle.id, formData);
    } else {
      result = await createArticle(formData);
    }

    if (result.success) {
      closeModal();
    } else {
      setError(result.error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ')) {
      await removeArticle(id);
    }
  };

  const handleImageUpload = (e, type = 'featured') => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier la taille (max 500KB pour éviter de surcharger Firestore)
    if (file.size > 500 * 1024) {
      setError('L\'image ne doit pas dépasser 500 Ko. Veuillez compresser l\'image.');
      return;
    }

    if (type === 'featured') {
      setUploadingImage(true);
    } else {
      setUploadingContentImage(true);
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'featured') {
        setFormData({ ...formData, featuredImage: reader.result });
        setUploadingImage(false);
      }
    };
    reader.onerror = () => {
      setError('Erreur lors de la lecture de l\'image');
      if (type === 'featured') {
        setUploadingImage(false);
      } else {
        setUploadingContentImage(false);
      }
    };
    reader.readAsDataURL(file);
  };

const openModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title,
        resume: article.resume || '',
        content: article.content || '',
        category: article.category,
        readTime: article.readTime || '3 min',
        featuredImage: article.featuredImage || ''
      });
    } else {
      setEditingArticle(null);
      setFormData({
        title: '',
        resume: '',
        content: '',
        category: categories[0],
        readTime: '3 min',
        featuredImage: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingArticle(null);
    setError('');
  };

  return (
    <AdminLayout title="Articles">
      <Container>
        <Header>
          <Title>Gestion des Articles</Title>
          <AddButton onClick={() => openModal()}>
            <Plus size={20} />
            Ajouter un article
          </AddButton>
        </Header>

        <Table>
          <thead>
            <tr>
              <Th>Titre</Th>
              <Th>Catégorie</Th>
              <Th>Date</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <Td colSpan="4" style={{ textAlign: 'center' }}>Chargement...</Td>
              </tr>
            ) : articles.length === 0 ? (
              <tr>
                <Td colSpan="4" style={{ textAlign: 'center' }}>Aucun article enregistré</Td>
              </tr>
            ) : (
              articles.map((article) => (
                <tr key={article.id}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={16} />
                      {article.title}
                    </div>
                  </Td>
                  <Td>
                    <CategoryBadge $category={article.category}>
                      {article.category}
                    </CategoryBadge>
                  </Td>
                  <Td>
                    {article.createdAt.toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </Td>
                  <Td>
                    <ActionButton onClick={() => openModal(article)}>
                      <Edit2 size={18} />
                    </ActionButton>
                    <ActionButton className="delete" onClick={() => handleDelete(article.id)}>
                      <Trash2 size={18} />
                    </ActionButton>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {isModalOpen && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingArticle ? 'Modifier' : 'Ajouter'} un article</h2>
                <CloseButton onClick={closeModal}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>

              <Form onSubmit={handleSubmit}>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                <FormGroup>
                  <Label>Titre</Label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Titre de l'article"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Résumé (2-3 phrases)</Label>
                  <TextArea
                    value={formData.resume}
                    onChange={(e) => {
                      if (e.target.value.length <= 300) {
                        setFormData({ ...formData, resume: e.target.value });
                      }
                    }}
                    placeholder="Résumé court de l'article (affiché sur la liste des actualités)"
                    maxLength={300}
                    required
                  />
                  <CharCount>{formData.resume.length}/300 caractères</CharCount>
                </FormGroup>

                <FormGroup>
                  <Label>Contenu de l'article</Label>
                  <SimpleEditor
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="Rédigez le contenu complet de l'article..."
                  />
                  <small style={{ color: 'var(--text-light)', marginTop: '0.5rem', display: 'block' }}>
                    💡 Astuce : Utilisez le bouton image dans la barre d'outils pour ajouter des images au corps de l'article. Les images sont automatiquement centrées (max 300 Ko).
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label>Catégorie</Label>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Temps de lecture</Label>
                  <Input
                    type="text"
                    value={formData.readTime}
                    onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                    placeholder="Ex: 3 min"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Image mise en avant (optionnel - max 500 Ko)</Label>
                  <ImageUploadBox 
                    $hasImage={!!formData.featuredImage}
                    onClick={() => document.getElementById('featured-image-input').click()}
                  >
                    <Input
                      id="featured-image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'featured')}
                      style={{ display: 'none' }}
                    />
                    {formData.featuredImage ? (
                      <img src={formData.featuredImage} alt="Aperçu" />
                    ) : (
                      <>
                        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📷</div>
                        <div>Cliquez pour ajouter une image</div>
                        <small style={{ color: 'var(--text-light)' }}>Format recommandé : 1200x800px</small>
                      </>
                    )}
                  </ImageUploadBox>
                  {uploadingImage && <small style={{ color: 'var(--primary-blue)' }}>Chargement...</small>}
                  {formData.featuredImage && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, featuredImage: '' })}
                      style={{ 
                        marginTop: '0.5rem', 
                        color: '#DC2626', 
                        fontSize: '0.85rem',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      ❌ Supprimer l'image
                    </button>
                  )}
                </FormGroup>

                <ButtonGroup>
                  <CancelButton type="button" onClick={closeModal}>
                    Annuler
                  </CancelButton>
                  <SubmitButton type="submit">
                    {editingArticle ? 'Modifier' : 'Publier'}
                  </SubmitButton>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminArticles;
